// Distance-based lap time-delta helpers, shared by TimeDeltaTrace and TrackMap.

// Display number for a lap, matching the lap list: timed laps renumbered 1..N
// with out/in laps skipped (raw lap_number counts those).
export function lapDisplayNumber(laps, lapIndex, fallback = null) {
  const timed = (laps || []).filter(l => !l.is_outlap && !l.is_inlap);
  const i = timed.findIndex(l => (l.index ?? l.lap_index) === lapIndex);
  return i >= 0 ? i + 1 : fallback;
}

// Pick the base (reference) lap for delta comparisons: the fastest selected
// lap across both drivers, Theoretical Best included (TB is always fastest
// when selected). An override of { session, lapIndex } wins if that lap is
// still selected and loaded — callers fall back to auto otherwise.
// Returns { trace, lapIndex, session } or null.
export function pickBaseLap(lapTraces, selectedLaps, lapTraces2, selectedLaps2, override = null) {
  if (override) {
    const traces = override.session === 2 ? lapTraces2 : lapTraces;
    const sel    = override.session === 2 ? selectedLaps2 : selectedLaps;
    if (sel?.includes(override.lapIndex) && traces?.[override.lapIndex]) {
      return { trace: traces[override.lapIndex], lapIndex: override.lapIndex, session: override.session };
    }
  }

  let best = null;
  const scan = (traces, sel, session) => {
    for (const idx of sel || []) {
      const tr = traces?.[idx];
      if (!tr) continue;
      if (!best || tr.lap_time_seconds < best.trace.lap_time_seconds) {
        best = { trace: tr, lapIndex: idx, session };
      }
    }
  };
  scan(lapTraces,  selectedLaps,  1);
  scan(lapTraces2, selectedLaps2, 2);
  return best;
}

// N-driver variant of pickBaseLap. `session` is 1 for the primary driver and
// slot+2 for each comparison driver (so the first comparison is session 2,
// matching the legacy two-driver encoding). Returns { trace, lapIndex, session }.
export function pickBaseLapMulti(lapTraces, selectedLaps, compareDrivers = [], override = null) {
  const driverForSession = (s) => {
    if (s === 1) return { traces: lapTraces, sel: selectedLaps };
    const d = compareDrivers.find(cd => cd.slot === s - 2);
    return d ? { traces: d.lapTraces, sel: d.selectedLaps } : null;
  };
  if (override) {
    const dv = driverForSession(override.session);
    if (dv?.sel?.includes(override.lapIndex) && dv.traces?.[override.lapIndex]) {
      return { trace: dv.traces[override.lapIndex], lapIndex: override.lapIndex, session: override.session };
    }
  }
  let best = null;
  const scan = (traces, sel, session) => {
    for (const idx of sel || []) {
      const tr = traces?.[idx];
      if (!tr) continue;
      if (!best || tr.lap_time_seconds < best.trace.lap_time_seconds) {
        best = { trace: tr, lapIndex: idx, session };
      }
    }
  };
  scan(lapTraces, selectedLaps, 1);
  compareDrivers.forEach(d => scan(d.lapTraces, d.selectedLaps, d.slot + 2));
  return best;
}

// Build a sorted dist→relative-time lookup from raw trace points.
export function buildLookup(tracePoints) {
  return tracePoints
    .filter(p => p.dist_m != null && p.time != null)
    .map(p => ({ distM: p.dist_m, relTime: p.time }))
    .sort((a, b) => a.distM - b.distM);
}

export function normalizeLookup(lookup) {
  if (!lookup.length) return lookup;
  const t0 = lookup[0].relTime;
  return lookup.map(p => ({ ...p, relTime: p.relTime - t0 }));
}

// Linear interpolation of relTime at a given distM using a sorted lookup array
export function interpolateRelTime(lookup, distM) {
  if (!lookup.length) return null;
  if (distM <= lookup[0].distM) return lookup[0].relTime;
  const last = lookup[lookup.length - 1];
  if (distM >= last.distM) return last.relTime;

  let lo = 0, hi = lookup.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (lookup[mid].distM <= distM) lo = mid; else hi = mid;
  }
  const span = lookup[hi].distM - lookup[lo].distM;
  if (span === 0) return lookup[lo].relTime;
  const t = (distM - lookup[lo].distM) / span;
  return lookup[lo].relTime + t * (lookup[hi].relTime - lookup[lo].relTime);
}
